import { api } from './api';

/**
 * The team's connected Stripe account (Stripe Connect P2; D-391, D-393).
 * Two doors to the same account: the team's (Settings → Payments) and
 * STGD's (the STGD section). The API decides who may use which.
 */

export interface StripeRequirementsView {
  currently_due: string[];
  past_due: string[];
  eventually_due: string[];
  pending_verification: string[];
  current_deadline: string | null;
  disabled_reason: string | null;
}

export interface StripeAccountView {
  connect: {
    platform_key: 'OK' | 'MISSING' | 'UNRECOGNIZED' | 'LIVE_OUTSIDE_PRODUCTION';
    mode: 'TEST' | 'LIVE' | null;
    oauth_available: boolean;
    connect_mode: 'ON' | 'OFF';
  };
  account: null | {
    id: string;
    stripe_account_id: string;
    livemode: boolean;
    status: 'ONBOARDING' | 'RESTRICTED' | 'ENABLED' | 'DISABLED' | 'DISCONNECTED';
    connection_method: 'OAUTH' | 'ACCOUNT_LINK';
    display_name: string | null;
    statement_descriptor: string | null;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    requirements: StripeRequirementsView;
    connected_at: string | null;
    connected_by: string | null;
    last_synced_at: string | null;
    dashboard_url: string;
  };
  online_payments_open: boolean;
  /** STGD's per-ticket fee (D-394), read-only here. */
  platform_fee: { today: string; current: { amount_cents: number; effective_on: string } | null; upcoming: { amount_cents: number; effective_on: string }[]; collecting: false };
}

export interface PlatformStripeAccountView extends StripeAccountView {
  history: { id: string; stripe_account_id: string; livemode: boolean; status: string; connection_method: string; connected_at: string | null; disconnected_at: string | null; disconnected_reason: string | null }[];
}

export type ConnectDoor = 'settings' | 'platform';

const base = (door: ConnectDoor) => (door === 'platform' ? '/api/admin/platform/stripe-account' : '/api/admin/payments/stripe-account');

export const connectEndpoints = {
  account: () => api.get<StripeAccountView>(base('settings')),
  platformAccount: () => api.get<PlatformStripeAccountView>(base('platform')),
  onboarding: (door: ConnectDoor, email: string | null = null) => api.post<{ url: string; expires_at: string | null; created: boolean }>(`${base(door)}/onboarding`, { email }),
  refresh: (door: ConnectDoor) => api.post<StripeAccountView>(`${base(door)}/refresh`, {}),
  oauthStart: (door: ConnectDoor) => api.post<{ url: string; expires_at: string }>(`${base(door)}/oauth/start`, {}),
  oauthComplete: (door: ConnectDoor, code: string, state: string) => api.post<StripeAccountView & { connected: string }>(`${base(door)}/oauth/complete`, { code, state }),
  disconnect: (reason: string) => api.post<PlatformStripeAccountView & { deauthorized: boolean; message: string | null }>(`${base('platform')}/disconnect`, { reason }),
};

/** Stripe's requirement codes in plain words; anything unlisted is shown tidied up rather than hidden. */
const REQUIREMENT_WORDS: Record<string, string> = {
  external_account: 'A bank account for payouts',
  'business_profile.url': 'The business website',
  'business_profile.mcc': 'What the business sells',
  'business_profile.product_description': 'A description of what the business sells',
  'tos_acceptance.date': 'Accepting Stripe’s terms',
  'tos_acceptance.ip': 'Accepting Stripe’s terms',
  'company.tax_id': 'The business tax ID (EIN)',
  'company.address.line1': 'The business address',
  'company.phone': 'The business phone number',
  'representative.verification.document': 'An ID document for the representative',
  'individual.verification.document': 'An ID document',
  'representative.dob.day': 'The representative’s date of birth',
  'representative.ssn_last_4': 'The last four digits of the representative’s SSN',
};

export function requirementWords(code: string): string {
  if (REQUIREMENT_WORDS[code]) return REQUIREMENT_WORDS[code];
  const tidy = code.replace(/^(company|individual|representative|business_profile|settings)\./, '').replaceAll('.', ' ').replaceAll('_', ' ');
  return tidy.charAt(0).toUpperCase() + tidy.slice(1);
}

export const STATUS_WORDS: Record<NonNullable<StripeAccountView['account']>['status'], string> = {
  ONBOARDING: 'Setup not finished',
  RESTRICTED: 'Needs attention',
  ENABLED: 'Ready',
  DISABLED: 'Charges stopped by Stripe',
  DISCONNECTED: 'Disconnected',
};
